// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';

import {
  borderRadius,
  fontSize,
  fontWeight,
  mediaMax,
  mediaMaxWithLayoutTheme,
  spacing,
} from 'presentations/views/styles';
import Dialog from 'presentations/views/components/atoms/Dialog';
import TextField from 'presentations/views/components/atoms/TextField';
import DialogSubmitField from 'presentations/views/components/molecules/DialogSubmitField';
import Transition from 'presentations/views/components/atoms/Transition';
import {
  blue01,
  blueGrayPale,
  gray06,
  primary,
} from 'presentations/views/styles/color';
import { ViewerItem } from 'presentations/api/schema/user';
import { unlimitedApiKeyDuration, useApiKeyDialog } from './hooks';
import Select from '../../atoms/Select';
import { ApiKeyItem } from 'presentations/api/schema/api_key';
import CopyToClipboard from 'react-copy-to-clipboard';
import Icon from '../../atoms/Icon';

interface Props {
  isOpen: boolean;
  viewer: ViewerItem;
  apiKey: ApiKeyItem | null;
  onClose?: any;
  onUpdate?: any;
  onCreate?: any;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const durationOptions = [7, 30, 90, 365, unlimitedApiKeyDuration].map(
    (days) => ({
      value: days,
      text:
        days === unlimitedApiKeyDuration
          ? t('never expires')
          : days <= 90
          ? t('days', {
              num: days,
            })
          : t('year', { num: days / 365 }),
    }),
  );

  const { state, actions } = useApiKeyDialog(props);

  return (
    <StyledDialog
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={t(props.apiKey ? 'edit title' : 'create title')}
      actions={
        <ButtonContainer>
          {state.rawApiKey ? (
            <CopyMenuList>
              <CopyMenuContainer>
                <Transition name="collapse" in={state.isCopied}>
                  <CopiedMenuLink>
                    <Icon name="check" fontSize="small" color="primary" />
                    <CopyMenuLinkTitle>{t('Copied')}</CopyMenuLinkTitle>
                  </CopiedMenuLink>
                </Transition>
                <CopyToClipboard text={state.rawApiKey} onCopy={actions.onCopy}>
                  <CopyMenuLink role="button">
                    <CopyMenuLinkTitle>{t('Copy')}</CopyMenuLinkTitle>
                  </CopyMenuLink>
                </CopyToClipboard>
              </CopyMenuContainer>
            </CopyMenuList>
          ) : (
            <DialogSubmitField
              isLoading={state.isLoading}
              onSubmit={actions.onSubmit}
              onCancel={props.onClose}
              isDisabled={!state.name}
            />
          )}
        </ButtonContainer>
      }
      disableMinWidth
      titleBottomMargin={'0'}
      scroll
    >
      <DialogBody>
        {state.rawApiKey ? (
          <>
            <Block>
              <Message>{t('description copy')}</Message>
            </Block>
            <OnetimeLinkContainer>
              <OnetimeLinkTitle>{t('key')}</OnetimeLinkTitle>
              {state.rawApiKey}
            </OnetimeLinkContainer>
          </>
        ) : (
          <>
            <Block>
              <Message>{t('description')}</Message>
            </Block>

            <Block>
              <TextFieldWrap
                name={'name'}
                value={state.name}
                label={t('name')}
                placeholder={t('name placeholder')}
                onChange={actions.onChange}
                errors={state.errors['name']}
                isRequired
              />
            </Block>

            {!props.apiKey && (
              <Block>
                <SelectWrap
                  name={'expiresDate'}
                  label={t('expiresDate')}
                  value={state.expiresDate}
                  width={400}
                  onChange={actions.onChange}
                  options={[...durationOptions]}
                  isNotRequired
                />
              </Block>
            )}
          </>
        )}
      </DialogBody>
    </StyledDialog>
  );
}

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    max-width: 540px;
    overflow-y: visible !important;
  }
`;

const ButtonContainer = styled.div`
  margin: 0 auto;
  display: flex;
  justify-content: center;
`;

const DialogBody = styled.div`
  margin: ${spacing(8)} ${spacing(4)};
`;

const Block = styled.div`
  margin-bottom: ${spacing(24)};
  :last-of-type {
    margin-bottom: 0;
  }

  ${mediaMax.tablet} {
    margin-bottom: ${spacing(14)};
  }
`;

const Message = styled.div`
  margin-top: ${spacing(16)};
  white-space: pre-wrap;
  font-size: ${fontSize(14)};
  font-weight: initial;

  ${mediaMax.tablet} {
    margin-top: ${spacing(8)};
    font-size: ${fontSize(12)};
  }
`;

const OnetimeLinkContainer = styled.div`
  padding: ${spacing(16)};
  margin-bottom: ${spacing(16)};
  background-color: ${blueGrayPale};
  border-radius: ${borderRadius(12)};
  font-size: ${fontSize(14)};
  word-break: break-all;

  ${mediaMax.tablet} {
    padding: ${spacing(14)};
    font-size: ${fontSize(12)};
  }
`;
const OnetimeLinkTitle = styled.div`
  margin-bottom: ${spacing(12)};
  font-size: ${fontSize(14)};
  font-weight: ${fontWeight('bold')};

  ${mediaMax.tablet} {
    margin-bottom: ${spacing(6)};
  }
`;

const SelectWrap = styled(Select)`
  ${(props) => mediaMaxWithLayoutTheme(props, 'desktop')} {
    width: 100%;
  }
`;

const TextFieldWrap = styled(TextField)`
  ${(props) => mediaMaxWithLayoutTheme(props, 'desktop')} {
    width: 100%;
  }
  margin-bottom: ${spacing(16)};
`;

const CopyMenuLink = styled.div`
  height: 52px;
  color: ${primary};
  display: flex;
  align-items: center;
  justify-content: center;
`;
const CopyMenuLinkTitle = styled.span`
  margin-left: ${spacing(8)};
  font-size: ${fontSize(13)};
  font-weight: ${fontWeight('bold')};
`;

const CopyMenuList = styled.div`
  height: 52px;
  border-radius: ${borderRadius()};
  background-color: ${gray06};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  :hover {
    border-radius: ${borderRadius(6)} 0 0 ${borderRadius(6)};
    background-color: ${blue01};
  }
`;
const CopyMenuContainer = styled.div`
  width: 240px;
  height: 52px;
  overflow: hidden;
  border-radius: ${borderRadius(6)} 0 0 ${borderRadius(6)};
  ${mediaMax.tablet} {
    width: 90%;
  }
`;

const CopiedMenuLink = styled.div`
  width: 240px;
  height: 52px;
  color: ${primary};
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${blue01};
  :hover {
    border-radius: ${borderRadius(6)} 0 0 ${borderRadius(6)};
  }
  ${mediaMax.tablet} {
    width: 100%;
  }
`;
